import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { TemplateMessageEditor } from "@/components/templates/TemplateMessageEditor";
import {
  messageTemplateService,
  CreateMessageTemplateRequest,
  EMessageTemplateType,
  MESSAGE_TEMPLATE_TYPE_LABEL,
} from "@/services/messageTemplate";

interface CreateMessageTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_FORM: CreateMessageTemplateRequest = {
  name: "",
  type: EMessageTemplateType.PAYMENT_REMINDER,
  body: "",
  variables: [],
  isActive: true,
};

export function CreateMessageTemplateModal({
  open,
  onOpenChange,
}: CreateMessageTemplateModalProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<CreateMessageTemplateRequest>(EMPTY_FORM);

  useEffect(() => {
    if (open) {
      setFormData(EMPTY_FORM);
    }
  }, [open]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateMessageTemplateRequest) =>
      messageTemplateService.create(data),
    onSuccess: () => {
      toast.success("Plantilla creada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error("El nombre de la plantilla es requerido");
      return;
    }
    if (!formData.body.trim()) {
      toast.error("El cuerpo del mensaje es requerido");
      return;
    }
    createMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva plantilla de mensaje</DialogTitle>
          <DialogDescription>
            Crea una plantilla reutilizable para tus mensajes automáticos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Recordatorio de pago"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>
              Tipo <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.type}
              onValueChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  type: val as EMessageTemplateType,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(EMessageTemplateType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {MESSAGE_TEMPLATE_TYPE_LABEL[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <TemplateMessageEditor
            id="body"
            body={formData.body}
            variables={formData.variables}
            onChange={(body, variables) =>
              setFormData((prev) => ({ ...prev, body, variables }))
            }
          />

          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
            <div>
              <Label htmlFor="isActive" className="text-sm">Plantilla activa</Label>
              <p className="text-xs text-muted-foreground">
                Solo las plantillas activas pueden usarse en reglas de envío.
              </p>
            </div>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isActive: checked }))
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={createMutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Crear plantilla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
